import React, { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import CollapsibleSection from "../CollapsibleSection";
import { CalendarIcon, PrintIcon } from "../../../../Icons";
import useConfirm from "../../../../hooks/useConfirm";
import {
  getStaffSchedule,
  upsertStaffScheduleSlot,
  removeStaffScheduleSlot,
} from "../../../../services/patientService";
import { PATIENT_SECTION_TONE, PATIENT_THEME } from "../../patientTheme";
import { STAFF_SCHEDULE_DAYS } from "./staffScheduleConstants";
import StaffScheduleRoleChip from "./StaffScheduleRoleChip";
import StaffScheduleTimeChip from "./StaffScheduleTimeChip";
import PatientAvatar from "../PatientAvatar";
import { slotForDay } from "./staffScheduleTime";
import { truncateStaffName } from "./staffScheduleDisplay";
import StaffScheduleTimeModal from "./StaffScheduleTimeModal";
import { staffSchedulePrintUrl } from "../../staffSchedulePrint";

const SCHEDULE_TONE = PATIENT_SECTION_TONE;

const normalizeChartId = (chartId) => {
  if (chartId == null || chartId === "") return undefined;
  const n = Number(chartId);
  return Number.isInteger(n) && n > 0 ? n : undefined;
};

const PatientStaffScheduleSection = ({
  patientId,
  chartId,
  canEdit,
  refreshKey = 0,
}) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalContext, setModalContext] = useState(null);
  const { confirm, confirmDialog } = useConfirm();

  const scopedChartId = normalizeChartId(chartId);

  const loadSchedule = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getStaffSchedule(
        patientId,
        scopedChartId ? { chartId: scopedChartId } : {},
      );
      setRows(res.result?.rows || res.rows || []);
    } catch (err) {
      toast.error(err.message || "Failed to load staff schedule");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [patientId, scopedChartId]);

  useEffect(() => {
    loadSchedule();
  }, [loadSchedule, refreshKey]);

  const openModal = (staffRow, dayOfWeek) => {
    const existingSlot = slotForDay(staffRow.slots, dayOfWeek);
    setModalContext({ staffRow, dayOfWeek, existingSlot });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setModalContext(null);
  };

  const handleSave = async (payload) => {
    if (!modalContext?.staffRow) return;
    setSaving(true);
    try {
      await upsertStaffScheduleSlot(patientId, {
        assignmentId: modalContext.staffRow.assignmentId,
        dayOfWeek: modalContext.dayOfWeek,
        ...payload,
        ...(scopedChartId ? { chartId: scopedChartId } : {}),
      });
      toast.success(
        modalContext.existingSlot ? "Schedule updated" : "Time added",
      );
      closeModal();
      await loadSchedule();
    } catch (err) {
      toast.error(err.message || "Failed to save schedule");
    } finally {
      setSaving(false);
    }
  };

  const handleClearSlot = async (slot) => {
    if (!canEdit || !slot?.id) return;
    const ok = await confirm({
      title: "Remove scheduled time?",
      message: "This will clear the weekly time slot for this day.",
      confirmText: "Remove",
      cancelText: "Cancel",
      tone: "danger",
    });
    if (!ok) return;
    try {
      await removeStaffScheduleSlot(patientId, slot.id);
      toast.success("Schedule time removed");
      await loadSchedule();
    } catch (err) {
      toast.error(err.message || "Failed to remove time");
    }
  };

  const handlePrint = () => {
    const url = staffSchedulePrintUrl(patientId, scopedChartId);
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <>
      <CollapsibleSection
        title="Staff Schedule"
        icon={<CalendarIcon size={14} />}
        tone={SCHEDULE_TONE}
        defaultOpen
        actions={
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handlePrint}
              className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-md border ${PATIENT_THEME.section.edit}`}
              title="Print staff schedule"
            >
              <PrintIcon size={12} />
              Print
            </button>
          </div>
        }
      >
        <div className="p-3 space-y-3">
          <div className="rounded-lg border border-indigo-100 bg-indigo-50/60 px-3 py-2 text-[12px] text-indigo-800">
            <span className="font-semibold">Recurring Weekly Schedule:</span>{" "}
            Set the time for each day. The schedule repeats every week. Only
            assigned staff appear below.
          </div>

          {loading ? (
            <div className="rounded-lg border border-slate-200 bg-white py-10 text-center text-sm text-slate-400 animate-pulse">
              Loading schedule...
            </div>
          ) : rows.length === 0 ? (
            <div className="rounded-lg border border-dashed border-slate-200 bg-white py-10 text-center">
              <p className="text-sm font-medium text-slate-600">
                No assigned staff yet
              </p>
              <p className="text-xs text-slate-400 mt-1">
                Assign staff in the Care Team section above — each assigned
                member will appear here for weekly scheduling.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-indigo-100 bg-white shadow-sm">
              <table className="min-w-[820px] w-full border-collapse table-fixed text-left">
                <colgroup>
                  <col className="w-[132px]" />
                  <col className="w-[52px]" />
                  {STAFF_SCHEDULE_DAYS.map((day) => (
                    <col key={day.value} className="w-[108px]" />
                  ))}
                </colgroup>
                <thead>
                  <tr className={PATIENT_THEME.tableHead.className}>
                    <th className="px-2 py-2 text-[11px] font-bold uppercase tracking-wide border-b border-indigo-100 sticky left-0 bg-indigo-50/95 z-10">
                      Staff Name
                    </th>
                    <th className="px-1 py-2 text-[11px] font-bold uppercase tracking-wide border-b border-indigo-100 text-center">
                      Role
                    </th>
                    {STAFF_SCHEDULE_DAYS.map((day) => (
                      <th
                        key={day.value}
                        className="px-1 py-2 text-[11px] font-bold uppercase tracking-wide border-b border-indigo-100 text-center"
                      >
                        {day.short}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => {
                    const name = truncateStaffName(row.firstName, row.lastName);
                    return (
                    <tr
                      key={row.assignmentId}
                      className="border-b border-slate-100 last:border-b-0 hover:bg-slate-50/50"
                    >
                      <td className="px-2 py-2 sticky left-0 bg-white z-[1] border-r border-slate-100">
                        <div className="flex items-center gap-1.5 min-w-0 max-w-[132px]">
                          <PatientAvatar
                            firstName={row.firstName}
                            lastName={row.lastName}
                            size="sm"
                          />
                          <span
                            className="text-[11px] font-semibold text-slate-900 truncate min-w-0"
                            title={name.full || undefined}
                          >
                            {name.display}
                          </span>
                        </div>
                      </td>
                      <td className="px-1 py-2 text-center align-middle">
                        <StaffScheduleRoleChip role={row.role} />
                      </td>
                      {STAFF_SCHEDULE_DAYS.map((day) => {
                        const slot = slotForDay(row.slots, day.value);
                        return (
                          <td
                            key={day.value}
                            className="px-1 py-2 align-top text-center"
                          >
                            {slot ? (
                              <div className="inline-flex flex-col items-center gap-1 w-full">
                                <StaffScheduleTimeChip slot={slot} />
                                {canEdit ? (
                                  <div className="flex items-center justify-center gap-1.5">
                                    <button
                                      type="button"
                                      onClick={() => openModal(row, day.value)}
                                      className={`text-[10px] font-semibold ${PATIENT_THEME.button.link}`}
                                    >
                                      Edit
                                    </button>
                                    <span className="text-slate-200">|</span>
                                    <button
                                      type="button"
                                      onClick={() => handleClearSlot(slot)}
                                      className="text-[10px] font-semibold text-red-600 hover:text-red-700"
                                    >
                                      Clear
                                    </button>
                                  </div>
                                ) : null}
                              </div>
                            ) : (
                              <div className="flex flex-col items-center gap-1 min-h-[72px] justify-center">
                                <span className="text-slate-300 text-sm leading-none">—</span>
                                {canEdit ? (
                                  <button
                                    type="button"
                                    onClick={() => openModal(row, day.value)}
                                    className={`text-[10px] font-semibold whitespace-nowrap ${PATIENT_THEME.button.link}`}
                                  >
                                    + Add Time
                                  </button>
                                ) : null}
                              </div>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <div className="rounded-lg border border-slate-200 bg-slate-50/80 px-3 py-2 text-[11px] text-slate-600">
            <span className="font-semibold text-slate-700">How to Add Time:</span>{" "}
            Click &quot;+ Add Time&quot; under any day to set a weekly time slot.
            One time range per day per staff member — use Edit to change an
            existing slot. Staff with the same role cannot have overlapping
            times on the same day.
          </div>
        </div>
      </CollapsibleSection>

      <StaffScheduleTimeModal
        isOpen={modalOpen}
        onClose={closeModal}
        onSave={handleSave}
        saving={saving}
        staffRow={modalContext?.staffRow}
        dayOfWeek={modalContext?.dayOfWeek}
        existingSlot={modalContext?.existingSlot}
        allRows={rows}
      />
      {confirmDialog}
    </>
  );
};

export default PatientStaffScheduleSection;
